// ============================================================================
// ENGLISH TRANSLATIONS - CHAPTER 2: EXODUS
// ============================================================================
// Levels 14-26 (13 levels, 39 questions)

export const chapter2Translations = {
  name: "Exodus",
  description: "The liberation of God's people... Knowing God transforms our lives",
  
  spiritualWisdom: {
    principle: "Knowledge of God liberates and transforms",
    scripture: "You shall know the truth, and the truth shall set you free (John 8:32)",
    application: "As Israel was freed from Egypt, knowledge of God frees us from spiritual slavery"
  },
  
  levels: {
    14: {
      name: "Moses Saved from the Waters",
      challenge: "A woman conceived and bore a son. She saw that he was beautiful...",
      easy: {
        question: "What did Moses' mother hide him in?",
        options: ["A cave", "A reed basket", "A wicker basket", "A house"],
        hint: "This container floated on the Nile",
        funFact: "🏺 The reed basket was coated with pitch to be waterproof!"
      },
      medium: {
        question: "Who found Moses in the reeds?",
        options: [
          "Pharaoh's daughter",
          "An Egyptian servant", 
          "His sister Miriam",
          "An Egyptian priest"
        ],
        hint: "A member of the Egyptian royal family",
        funFact: "👑 Ironically, it's Pharaoh's family who saves the one who will defy him!"
      },
      hard: {
        question: "How does Divine Providence use even our enemies?",
        options: [
          "By converting them",
          "By orchestrating circumstances to accomplish His purposes",
          "By punishing them",
          "By ignoring them"
        ],
        hint: "It's about seeing God's hand in events",
        funFact: "✨ Moses grew up in the palace of the one who oppressed his people!"
      }
    },
    
    15: {
      name: "The Burning Bush",
      challenge: "The angel of the Lord appeared to him in a flame of fire...",
      easy: {
        question: "What should Moses remove before the burning bush?",
        options: ["His jewelry", "His sandals", "His cloak", "His turban"],
        hint: "What we wear on our feet",
        funFact: "👡 Removing shoes expresses respect on holy ground!"
      },
      medium: {
        question: "What name does God reveal to Moses?",
        options: [
          "The Eternal",
          "I AM WHO I AM", 
          "The Almighty",
          "The Creator"
        ],
        hint: "A name that expresses absolute existence",
        funFact: "🔥 YHWH (I Am) reveals God's eternal essence!"
      },
      hard: {
        question: "What does the bush that burns without being consumed teach us?",
        options: [
          "Magic exists",
          "God transcends natural laws",
          "Fire is sacred",
          "Plants are magical"
        ],
        hint: "It's about divine nature that surpasses our understanding",
        funFact: "🌿 The bush symbolizes that divine presence sanctifies without destroying!"
      }
    },

    16: {
      name: "The Ten Plagues of Egypt",
      challenge: "Pharaoh hardened his heart and did not listen to them...",
      easy: {
        question: "What was the first plague of Egypt?",
        options: ["The frogs", "Water turned to blood", "The flies", "The lice"],
        hint: "It affected the Nile, source of life for Egypt",
        funFact: "🩸 The Nile was worshipped as a god, and God showed His superiority!"
      },
      medium: {
        question: "What was the last and most terrible of the plagues?",
        options: [
          "The hail",
          "The darkness", 
          "The death of the firstborn",
          "The locusts"
        ],
        hint: "This plague directly affected families",
        funFact: "😢 This plague finally broke Pharaoh's pride and freed Israel!"
      },
      hard: {
        question: "Why does God harden Pharaoh's heart?",
        options: [
          "To punish him",
          "To manifest His glory and power",
          "Out of wickedness",
          "To prolong suffering"
        ],
        hint: "It's about revealing who the true God is",
        funFact: "⚡ The plagues demonstrate that YHWH is more powerful than all Egyptian gods!"
      }
    },

    17: {
      name: "The Passover",
      challenge: "Take a lamb... and put its blood on the doorposts...",
      easy: {
        question: "Where should the Israelites put the lamb's blood?",
        options: [
          "On their clothes",
          "On the lintels and doorposts",
          "On their foreheads",
          "In cups"
        ],
        hint: "At the entrance of their houses",
        funFact: "🩸 The blood on the door was the sign of divine protection!"
      },
      medium: {
        question: "How should they eat the Passover lamb?",
        options: [
          "Sitting comfortably",
          "With family only",
          "With loins girded, sandals on feet, staff in hand",
          "In silence"
        ],
        hint: "Ready for departure",
        funFact: "🚶 They had to be ready to leave quickly toward freedom!"
      },
      hard: {
        question: "How does the Passover Lamb prefigure Christ?",
        options: [
          "By its whiteness",
          "By its substitutionary sacrifice that saves from death",
          "By its gentleness",
          "By its age"
        ],
        hint: "It's about the sacrifice that spares from condemnation",
        funFact: "🐑 Jesus is called 'the Lamb of God who takes away the sin of the world'!"
      }
    },

    18: {
      name: "The Crossing of the Red Sea",
      challenge: "Moses stretched out his hand over the sea...",
      easy: {
        question: "How did the Israelites cross the sea?",
        options: [
          "By boat",
          "Swimming", 
          "On dry ground",
          "On rafts"
        ],
        hint: "The seabed was passable",
        funFact: "🌊 God transformed an obstacle into a path of liberation!"
      },
      medium: {
        question: "What happened to the Egyptians who pursued them?",
        options: [
          "They turned back",
          "They were swallowed by the waters",
          "They got lost",
          "They joined Israel"
        ],
        hint: "The waters fell back on them",
        funFact: "⚔️ The world's most powerful army was destroyed in an instant!"
      },
      hard: {
        question: "What does this crossing symbolize in our spiritual life?",
        options: [
          "Fleeing from problems",
          "The passage from slavery of sin to freedom in God",
          "Israel's superiority",
          "The destruction of enemies"
        ],
        hint: "It's about spiritual transformation",
        funFact: "🗽 The Red Sea represents baptism: death to the old, new life!"
      }
    },

    19: {
      name: "Manna in the Wilderness",
      challenge: "What is it? For they did not know what it was...",
      easy: {
        question: "How long did the manna fall?",
        options: ["10 years", "20 years", "40 years", "50 years"],
        hint: "The same number as the years in the wilderness",
        funFact: "🍞 For 40 years, God miraculously fed 2 million people!"
      },
      medium: {
        question: "What happened if you kept manna for more than one day?",
        options: [
          "It became more tasty",
          "It multiplied",
          "It rotted and smelled bad",
          "It hardened"
        ],
        hint: "Except Friday for the Sabbath",
        funFact: "🪱 God thus taught daily dependence on Him!"
      },
      hard: {
        question: "How does manna teach us trust?",
        options: [
          "By storing for the future",
          "By relying on God's daily providence",
          "By working harder",
          "By sharing equally"
        ],
        hint: "It's about trusting day by day",
        funFact: "🙏 'Give us this day our daily bread' comes from this experience!"
      }
    },

    20: {
      name: "Water from the Rock",
      challenge: "You shall strike the rock, and water will come out of it...",
      easy: {
        question: "What should Moses strike the rock with?",
        options: ["His hand", "His rod", "A stone", "A hammer"],
        hint: "The instrument of Moses' miracles",
        funFact: "⚡ Moses' rod was the instrument of divine power!"
      },
      medium: {
        question: "Why did the people murmur against Moses?",
        options: [
          "There was no water to drink",
          "Food was lacking",
          "The road was too long",
          "It was too hot"
        ],
        hint: "A vital need in the desert",
        funFact: "💧 Water is more essential than food for survival!"
      },
      hard: {
        question: "What does the water from the rock represent spiritually?",
        options: [
          "Natural resources",
          "Spiritual life that springs from Christ, the Rock",
          "The reward of efforts",
          "Divine magic"
        ],
        hint: "Paul says this rock was Christ",
        funFact: "🗿 Christ is the Rock struck from which eternal life springs!"
      }
    },

    21: {
      name: "Mount Sinai",
      challenge: "All Mount Sinai was in smoke, because the Lord had descended upon it...",
      easy: {
        question: "How many commandments did God give?",
        options: ["5", "7", "10", "12"],
        hint: "A round and perfect number",
        funFact: "📜 The Ten Commandments are humanity's moral foundation!"
      },
      medium: {
        question: "What were the commandments written on?",
        options: [
          "Papyrus",
          "Parchment",
          "Two stone tablets",
          "Bronze"
        ],
        hint: "A durable material written by God's finger",
        funFact: "✍️ Written by God Himself to show their eternal importance!"
      },
      hard: {
        question: "What is the purpose of divine commandments?",
        options: [
          "To punish men",
          "To reveal God's holiness and guide toward happiness",
          "To create complex rules",
          "To separate peoples"
        ],
        hint: "It's about revealing divine character and the path of good",
        funFact: "⚖️ The commandments reveal love: love of God and neighbor!"
      }
    },

    22: {
      name: "The Golden Calf",
      challenge: "Make us a god who shall go before us...",
      easy: {
        question: "What did Aaron make the golden calf with?",
        options: [
          "Gold coins",
          "The people's golden earrings",
          "Precious jewelry",
          "Desert gold"
        ],
        hint: "Personal jewelry given by the people",
        funFact: "👂 The people gave their jewelry to create their own idol!"
      },
      medium: {
        question: "What was Moses' reaction when he saw the calf?",
        options: [
          "He wept",
          "He prayed",
          "He broke the tablets of the law",
          "He only got angry"
        ],
        hint: "A symbolic gesture of the broken covenant",
        funFact: "💥 Breaking the tablets symbolized that the covenant was broken!"
      },
      hard: {
        question: "Why is idolatry so spiritually dangerous?",
        options: [
          "It's expensive",
          "It turns the heart from the true God to creatures",
          "It divides families",
          "It's forbidden"
        ],
        hint: "It's about giving to the creature what belongs to the Creator",
        funFact: "💔 Idolatry breaks the exclusive relationship with the living God!"
      }
    },

    23: {
      name: "The Tabernacle",
      challenge: "Let them make me a sanctuary, and I will dwell among them...",
      easy: {
        question: "What was the Israelites' sacred tent called?",
        options: ["The Temple", "The Tabernacle", "The Sanctuary", "The Ark"],
        hint: "The name means 'tent of meeting'",
        funFact: "⛺ The Tabernacle was God's 'mobile home' with His people!"
      },
      medium: {
        question: "What did the Ark of the Covenant contain?",
        options: [
          "The high priest's jewelry",
          "The tablets of the law, manna, Aaron's rod",
          "Gold coins",
          "Torah scrolls"
        ],
        hint: "Three objects symbolizing the covenant with God",
        funFact: "📦 The Ark contained the 'memories' of divine faithfulness!"
      },
      hard: {
        question: "What does the Tabernacle symbolize in our relationship with God?",
        options: [
          "A religious building",
          "God's presence dwelling among and in us",
          "A place of sacrifice only",
          "A national symbol"
        ],
        hint: "It's about accessible divine presence",
        funFact: "🏠 'Your body is the temple of the Holy Spirit' according to Paul!"
      }
    },

    24: {
      name: "The High Priest Aaron",
      challenge: "You shall consecrate Aaron and his sons to be my priests...",
      easy: {
        question: "Who was Israel's first high priest?",
        options: ["Moses", "Aaron", "Joshua", "Eleazar"],
        hint: "Moses' brother",
        funFact: "👨‍⚕️ Aaron founded the priestly line that lasted over 1000 years!"
      },
      medium: {
        question: "What did the high priest wear on his chest?",
        options: [
          "A golden cross",
          "The breastplate with twelve precious stones",
          "A medallion",
          "A golden chain"
        ],
        hint: "Twelve stones representing the twelve tribes",
        funFact: "💎 The high priest symbolically carried all Israel on his heart!"
      },
      hard: {
        question: "How does Jesus fulfill the role of High Priest?",
        options: [
          "By wearing sacred garments",
          "By offering His own blood once for all",
          "By serving in the Temple",
          "By descending from Aaron"
        ],
        hint: "It's about a perfect and final sacrifice",
        funFact: "✝️ Jesus is eternal High Priest according to the order of Melchizedek!"
      }
    },

    25: {
      name: "The Sacrifices",
      challenge: "If any of you brings an offering to the Lord...",
      easy: {
        question: "What was the daily sacrifice at the Tabernacle?",
        options: [
          "The peace offering",
          "The morning and evening burnt offering",
          "The sin offering",
          "The grain offering"
        ],
        hint: "Twice a day, morning and evening",
        funFact: "🔥 The fire on the altar must never go out!"
      },
      medium: {
        question: "What did the laying on of hands on the animal symbolize?",
        options: [
          "Blessing",
          "Transfer of sins to the victim",
          "Affection for the animal",
          "Consecration"
        ],
        hint: "A gesture of substitution",
        funFact: "🤲 The animal died in place of the sinner - principle of substitution!"
      },
      hard: {
        question: "How do these sacrifices prefigure Christ's work?",
        options: [
          "By their number",
          "By substitution: the innocent dies for the guilty",
          "By their cost",
          "By their beauty"
        ],
        hint: "It's about the principle of the innocent taking the place of the guilty",
        funFact: "🐑 'He was wounded for our transgressions, bruised for our iniquities'!"
      }
    },

    26: {
      name: "At the Borders of the Promised Land",
      challenge: "I will send an angel before you, and I will drive out the Canaanites...",
      easy: {
        question: "How many spies were sent to explore the Promised Land?",
        options: ["10", "12", "20", "40"],
        hint: "One per tribe of Israel",
        funFact: "🕵️ Only 2 of the 12 spies made an encouraging report!"
      },
      medium: {
        question: "Which spies encouraged the people to enter the land?",
        options: [
          "Aaron and Moses",
          "Joshua and Caleb",
          "Gad and Asher",
          "Reuben and Simeon"
        ],
        hint: "Two men of faith who saw God's promises",
        funFact: "🦁 Joshua and Caleb were the only ones of their generation to enter Canaan!"
      },
      hard: {
        question: "Why did Israel wander 40 years in the wilderness?",
        options: [
          "To punish them",
          "Because of their unbelief in divine promises",
          "To train them for battle",
          "To find the right path"
        ],
        hint: "It's about lack of trust in God",
        funFact: "🏜️ An entire generation had to die because of unbelief!"
      }
    }
  }
};

export default chapter2Translations;